import {observer} from 'mobx-react-lite';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {v4 as uuid} from 'uuid';
import {EAutocompleteOption} from 'webapp-lib/Components';
import {useDebouncedValue} from 'webapp-lib/utils';

import {ContactEmailAddressWithInvolvements} from '@lexian/api/contacts-api';
import {
  combineProps,
  convertPropsForField,
  EFProps,
  useController,
} from '_/utilities/form/controlled-components';
import {PaginatedItemLoader} from '_/utilities/simple-fetch';
import {
  defaultPaginationArguments,
  PaginationDirection,
  PaginationMode,
} from '_/utilities/types';

import {ETagsAutocomplete} from './ETagsAutocomplete';
import {
  CasusContactTagsAutocompleteProps as CasusEmailAddressTagsAutocompleteProps,
  CustomOnChangeTypeParams,
} from './types';
import {
  buildSearchContactEmailAddressesWithInvolvments,
  CONTACT_CATEGORIES,
  mapContactEmailAddressToOption,
} from './utils';

const UCasusEmailAddressTagsAutocomplete = ({
  label,
  name,
  value,
  onChange,
  casusId,
  alreadySelectedOptions,
  ...otherProps
}: CasusEmailAddressTagsAutocompleteProps): React.ReactElement => {
  const [debounceSearchQuery, searchQuery, setSearchQuery] =
    useDebouncedValue('');
  const [selectedEmailAddresses, setSelectedEmailAddresses] =
    useState<ContactEmailAddressWithInvolvements[]>(value);
  const [contactEmailAddressesStore] = useState(
    () =>
      new PaginatedItemLoader(
        buildSearchContactEmailAddressesWithInvolvments(
          casusId,
          alreadySelectedOptions
        )
      )
  );

  useEffect(() => {
    contactEmailAddressesStore.fetch(defaultPaginationArguments, {
      searchQuery: debounceSearchQuery,
    });
  }, [debounceSearchQuery, contactEmailAddressesStore]);

  const options = useMemo(
    () =>
      (contactEmailAddressesStore.loadable.data ?? []).map(({item}) =>
        mapContactEmailAddressToOption(item)
      ),
    [contactEmailAddressesStore.loadable.data]
  );

  const loadMoreBefore = useCallback(async () => {
    if (contactEmailAddressesStore.loadable.hasMoreBefore) {
      await contactEmailAddressesStore.fetchMore(
        {
          mode: PaginationMode.Browse,
          direction: PaginationDirection.Backward,
        },
        {searchQuery}
      );
    }
  }, [contactEmailAddressesStore, searchQuery]);

  const loadMoreAfter = useCallback(async () => {
    if (contactEmailAddressesStore.loadable.hasMoreAfter) {
      await contactEmailAddressesStore.fetchMore(
        {mode: PaginationMode.Browse, direction: PaginationDirection.Forward},
        {searchQuery}
      );
    }
  }, [contactEmailAddressesStore, searchQuery]);

  const handleChange = useCallback(
    (event: CustomOnChangeTypeParams<EAutocompleteOption>) => {
      const selectedOptions = event.target.value;
      const contactEmailAddresses = selectedOptions
        .filter((option) => option.value)
        .map((option): ContactEmailAddressWithInvolvements => {
          const existingContact = selectedEmailAddresses.find(
            (contact) => contact.id === option.value
          );
          if (existingContact) {
            return existingContact;
          }

          return (
            contactEmailAddressesStore.loadable.data?.find(
              ({item}) => item.id === option.value
            )?.item ?? {
              id: uuid(),
              contactName: option.value,
              emailAddress: option.value,
              category: 'OTHERS',
              displayName: option.value,
              involvements: [],
            }
          );
        });
      setSelectedEmailAddresses(contactEmailAddresses);
      onChange({target: {name, value: contactEmailAddresses}});
    },
    [
      contactEmailAddressesStore.loadable.data,
      selectedEmailAddresses,
      onChange,
      name,
    ]
  );

  return (
    <ETagsAutocomplete
      {...otherProps}
      categories={CONTACT_CATEGORIES}
      hasMoreAfter={contactEmailAddressesStore.loadable.hasMoreAfter}
      hasMoreBefore={contactEmailAddressesStore.loadable.hasMoreBefore}
      label={label}
      options={options}
      value={value.map((v) => ({value: v.id, displayText: v.displayName}))}
      allowCustomInput
      clearOnSelection
      noOptionFiltering
      onChange={handleChange}
      onLoadMoreAfter={loadMoreAfter}
      onLoadMoreBefore={loadMoreBefore}
      onSearchQueryChange={setSearchQuery}
    />
  );
};
export const CasusEmailAddressTagsAutocomplete = observer(
  UCasusEmailAddressTagsAutocomplete
);

export const EFCasusEmailAddressTagsAutocomplete = ({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  customValue,
  customOnChange,
  ...elementProps
}: EFProps<CasusEmailAddressTagsAutocompleteProps>): React.ReactElement => {
  const formPropsForField = convertPropsForField(
    useController({name, rules, shouldUnregister, defaultValue, control})
  );
  const combinedProps = combineProps(formPropsForField, elementProps, {
    customValue,
    customOnChange,
  });
  return <CasusEmailAddressTagsAutocomplete {...combinedProps} />;
};
